import { useState, useCallback } from 'react';
import { Message } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface HistoryState {
  isLoadingHistory: boolean;
  historyError: string | null;
  totalMessages: number;
  hasMore: boolean;
}

export interface HistoryActions {
  loadHistory: (email: string, limit?: number, offset?: number) => Promise<Message[]>;
  clearHistory: (email: string) => Promise<boolean>;
}

export function useHistory(): HistoryState & HistoryActions {
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [totalMessages, setTotalMessages] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const loadHistory = useCallback(
    async (email: string, limit: number = 100, offset: number = 0): Promise<Message[]> => {
      if (!email) {
        setHistoryError('Email is required');
        return [];
      }

      setIsLoadingHistory(true);
      setHistoryError(null);

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/messages/history?email=${encodeURIComponent(email)}&limit=${limit}&offset=${offset}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to load history: ${response.status}`);
        }

        const data = (await response.json()) as {
          success: boolean;
          data?: {
            messages: Message[];
            total: number;
            has_more: boolean;
          };
          error?: string;
        };

        if (!data.success || !data.data) {
          throw new Error(data.error || 'Failed to load history');
        }

        setTotalMessages(data.data.total);
        setHasMore(data.data.has_more);

        console.log('[History] Loaded', data.data.messages.length, 'messages of', data.data.total, 'total');

        return data.data.messages;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load history';
        setHistoryError(errorMessage);
        console.error('[History] Load error:', error);
        return [];
      } finally {
        setIsLoadingHistory(false);
      }
    },
    []
  );

  const clearHistory = useCallback(async (email: string): Promise<boolean> => {
    if (!email) {
      setHistoryError('Email is required');
      return false;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/messages/history?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to clear history: ${response.status}`);
      }

      const data = (await response.json()) as {
        success: boolean;
        data?: { deleted: number };
        error?: string;
      };

      if (!data.success) {
        throw new Error(data.error || 'Failed to clear history');
      }

      console.log('[History] Cleared', data.data?.deleted || 0, 'messages');
      setTotalMessages(0);
      setHasMore(false);

      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to clear history';
      setHistoryError(errorMessage);
      console.error('[History] Clear error:', error);
      return false;
    }
  }, []);

  return {
    isLoadingHistory,
    historyError,
    totalMessages,
    hasMore,
    loadHistory,
    clearHistory,
  };
}
